import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, Clock, Eye, Tag, ArrowRight, ChevronLeft, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const S = {
  fontFace: `
    @import url('https://fonts.googleapis.com/css2?family=Clash+Display:wght@500;600;700&family=Satoshi:wght@300;400;500;700&display=swap');
  `,
  keyframes: `
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    @keyframes pulse2 { 0%,100%{opacity:1} 50%{opacity:0.35} }

    .bl-fade  { animation: fadeUp 0.55s ease both; }
    .bl-fade1 { animation-delay: 0.08s; }
    .bl-fade2 { animation-delay: 0.16s; }
    .bl-fade3 { animation-delay: 0.24s; }

    .bl-card:hover .bl-card-img  { transform: scale(1.04); }
    .bl-card:hover .bl-card-arrow { transform: translateX(4px); }
    .bl-card:hover { border-color: #ff5a00 !important; box-shadow: 0 8px 32px rgba(255,90,0,0.10); }

    .bl-tag:hover { background: rgba(255,90,0,0.12) !important; color: #ff5a00 !important; border-color: rgba(255,90,0,0.3) !important; }
    .bl-search-input:focus { border-color: #ff5a00 !important; }
    .bl-read-btn:hover { background: #e04e00 !important; transform: translateY(-1px); }

    .bl-skeleton {
      background: linear-gradient(90deg, #f0ede8 25%, #e8e4de 50%, #f0ede8 75%);
      background-size: 600px 100%;
      animation: shimmer 1.4s infinite;
      border-radius: 10px;
    }

    ::placeholder { color: #bbb !important; }
  `,

  page:    { fontFamily: "'Satoshi', sans-serif", background: '#f5f4f0', minHeight: '100vh', color: '#1a1a1a' },

  /* Hero */
  hero: { position: 'relative', overflow: 'hidden', padding: '100px 24px 80px', textAlign: 'center', background: '#fff', borderBottom: '1px solid #ece9e4' },
  heroGrid: { position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,90,0,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,90,0,0.05) 1px,transparent 1px)', backgroundSize: '56px 56px' },
  heroGlow: { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '560px', height: '320px', background: 'radial-gradient(ellipse,rgba(255,90,0,0.09) 0%,transparent 70%)', pointerEvents: 'none' },
  heroBadge: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(255,90,0,0.08)', border: '1px solid rgba(255,90,0,0.22)', borderRadius: '100px', padding: '5px 16px', fontSize: '11px', fontWeight: 700, color: '#ff5a00', marginBottom: '20px', letterSpacing: '0.1em', textTransform: 'uppercase' },
  heroDot:   { width: '7px', height: '7px', borderRadius: '50%', background: '#ff5a00', animation: 'pulse2 2s infinite' },
  heroTitle: { fontFamily: "'Clash Display', sans-serif", fontSize: 'clamp(36px, 5vw, 62px)', fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.03em', color: '#111', marginBottom: '14px' },
  heroAccent:{ color: '#ff5a00' },
  heroSub:   { fontSize: '16px', color: '#888', maxWidth: '480px', margin: '0 auto 40px', lineHeight: 1.75 },

  /* Search */
  searchWrap: { maxWidth: '480px', margin: '0 auto', position: 'relative', zIndex: 1 },
  searchBox:  { display: 'flex', alignItems: 'center', background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '14px', padding: '0 16px', gap: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  searchInput:{ flex: 1, border: 'none', outline: 'none', fontSize: '14px', color: '#111', padding: '13px 0', fontFamily: "'Satoshi', sans-serif", background: 'transparent' },

  /* Tags filter */
  tagsRow:   { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '28px' },
  tag: (active) => ({
    padding: '5px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 600,
    border: `1.5px solid ${active ? '#ff5a00' : '#e0ddd8'}`,
    background: active ? 'rgba(255,90,0,0.08)' : '#fff',
    color: active ? '#ff5a00' : '#888',
    cursor: 'pointer', transition: 'all 0.15s',
    fontFamily: "'Satoshi', sans-serif",
  }),

  /* Grid */
  section: { padding: '64px 24px 80px' },
  inner:   { maxWidth: '1160px', margin: '0 auto' },

  /* Featured card */
  featuredCard: { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '24px', overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: '28px', transition: 'border-color 0.2s, box-shadow 0.2s', cursor: 'pointer' },
  featuredImg:  { height: '360px', overflow: 'hidden', position: 'relative' },
  featuredImgEl:{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.45s ease' },
  featuredBody: { padding: '44px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  featuredLabel:{ fontSize: '11px', fontWeight: 700, color: '#ff5a00', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '14px' },
  featuredTitle:{ fontFamily: "'Clash Display', sans-serif", fontSize: 'clamp(22px, 2.5vw, 32px)', fontWeight: 700, color: '#111', lineHeight: 1.18, letterSpacing: '-0.02em', marginBottom: '14px' },
  featuredDesc: { fontSize: '14px', color: '#888', lineHeight: 1.75, marginBottom: '24px' },
  featuredMeta: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' },
  metaAvatar:   { width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,90,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#ff5a00', flexShrink: 0, overflow: 'hidden' },
  metaText:     { fontSize: '13px', color: '#555' },
  metaName:     { fontWeight: 700, color: '#111' },
  readBtn:      { display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#ff5a00', border: 'none', borderRadius: '12px', padding: '12px 22px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', transition: 'background 0.15s, transform 0.12s', fontFamily: "'Satoshi', sans-serif', alignSelf: 'flex-start'" },

  /* Cards grid */
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '18px' },
  card:  { background: '#fff', border: '1.5px solid #ece9e4', borderRadius: '22px', overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s' },
  cardImgWrap: { height: '210px', overflow: 'hidden', position: 'relative' },
  cardImg:     { width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' },
  cardImgPlaceholder: { width: '100%', height: '100%', background: 'linear-gradient(135deg,rgba(255,90,0,0.07),rgba(180,180,180,0.07))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardBody:    { padding: '22px 24px 24px' },
  cardTags:    { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' },
  cardTagPill: { fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ff5a00', background: 'rgba(255,90,0,0.07)', border: '1px solid rgba(255,90,0,0.18)', borderRadius: '6px', padding: '2px 8px' },
  cardTitle:   { fontFamily: "'Clash Display', sans-serif", fontSize: '17px', fontWeight: 700, color: '#111', lineHeight: 1.28, letterSpacing: '-0.01em', marginBottom: '8px' },
  cardDesc:    { fontSize: '13px', color: '#888', lineHeight: 1.7, marginBottom: '16px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardFooter:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardAuthor:  { display: 'flex', alignItems: 'center', gap: '9px' },
  cardAvatar:  { width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(255,90,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#ff5a00', flexShrink: 0, overflow: 'hidden' },
  cardAuthorName: { fontSize: '12px', fontWeight: 700, color: '#333' },
  cardDate:       { fontSize: '11px', color: '#bbb' },
  cardArrow:      { width: '32px', height: '32px', borderRadius: '50%', background: '#f5f4f0', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.18s' },

  /* Empty / loading */
  empty: { textAlign: 'center', padding: '80px 24px' },
  emptyIcon: { fontSize: '52px', marginBottom: '16px' },
  emptyTitle: { fontFamily: "'Clash Display', sans-serif", fontSize: '22px', fontWeight: 700, color: '#111', marginBottom: '8px' },
  emptySub:   { fontSize: '14px', color: '#aaa' },

  /* Pagination */
  paginationRow: { display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '48px' },
  pageBtn: (active) => ({
    width: '40px', height: '40px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
    border: `1.5px solid ${active ? '#ff5a00' : '#e0ddd8'}`,
    background: active ? '#ff5a00' : '#fff',
    color: active ? '#fff' : '#555',
    cursor: 'pointer', transition: 'all 0.15s', fontFamily: "'Satoshi', sans-serif",
  }),

  /* ── Detail page ── */
  detailPage: { fontFamily: "'Satoshi', sans-serif", background: '#f5f4f0', minHeight: '100vh' },
  detailHero: { position: 'relative', height: '480px', overflow: 'hidden', background: '#111' },
  detailCover:{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.75 },
  detailHeroOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)' },
  detailHeroContent:{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '40px 32px', maxWidth: '860px', margin: '0 auto' },
  detailHeroTag: { display: 'inline-block', background: '#ff5a00', borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, color: '#fff', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' },
  detailTitle:  { fontFamily: "'Clash Display', sans-serif", fontSize: 'clamp(26px,4vw,46px)', fontWeight: 700, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.025em', marginBottom: '18px' },
  detailMeta:   { display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' },
  detailAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,90,0,0.2)', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 700, color: '#ff5a00', overflow: 'hidden', flexShrink: 0 },
  detailAuthorName: { fontSize: '14px', fontWeight: 700, color: '#fff' },
  detailAuthorRole: { fontSize: '12px', color: 'rgba(255,255,255,0.65)' },
  detailMetaDivider:{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.2)' },
  detailMetaItem:   { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.7)' },

  detailBody: { maxWidth: '780px', margin: '0 auto', padding: '56px 24px 80px' },
  detailDesc: { fontSize: '18px', color: '#555', lineHeight: 1.8, marginBottom: '36px', paddingBottom: '36px', borderBottom: '1px solid #ece9e4', fontStyle: 'italic' },
  detailContent: { fontSize: '15px', color: '#333', lineHeight: 1.85 },

  backBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '13px', fontWeight: 600, color: '#ff5a00', background: 'rgba(255,90,0,0.07)', border: '1.5px solid rgba(255,90,0,0.2)', borderRadius: '100px', padding: '7px 16px', cursor: 'pointer', marginBottom: '0', transition: 'background 0.15s', fontFamily: "'Satoshi', sans-serif", textDecoration: 'none' },
  backBar: { background: '#fff', borderBottom: '1px solid #ece9e4', padding: '14px 24px' },
  backBarInner: { maxWidth: '860px', margin: '0 auto' },

  detailTagsRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '40px', paddingTop: '32px', borderTop: '1px solid #ece9e4' },
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}
function readTime(content = '') {
  const words = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

/* ─── Skeleton card ───────────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div style={{ ...S.card, pointerEvents: 'none' }}>
    <div style={{ ...S.cardImgWrap }}><div className="bl-skeleton" style={{ width: '100%', height: '100%' }} /></div>
    <div style={{ padding: '22px 24px 24px' }}>
      <div className="bl-skeleton" style={{ height: '12px', width: '60px', marginBottom: '10px' }} />
      <div className="bl-skeleton" style={{ height: '18px', width: '90%', marginBottom: '6px' }} />
      <div className="bl-skeleton" style={{ height: '18px', width: '70%', marginBottom: '14px' }} />
      <div className="bl-skeleton" style={{ height: '12px', width: '100%', marginBottom: '6px' }} />
      <div className="bl-skeleton" style={{ height: '12px', width: '80%', marginBottom: '18px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div className="bl-skeleton" style={{ width: '30px', height: '30px', borderRadius: '50%' }} />
          <div className="bl-skeleton" style={{ height: '12px', width: '80px' }} />
        </div>
        <div className="bl-skeleton" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
      </div>
    </div>
  </div>
);

/* ─── Avatar ──────────────────────────────────────────────────────────────── */
const Avatar = ({ src, name, size = 30 }) => {
  if (src) return <img src={src} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
  return <span style={{ fontSize: size * 0.4 }}>{initials(name)}</span>;
};

/* ══════════════════════════════════════════════════════════════════════════════
   BLOG DETAIL PAGE
══════════════════════════════════════════════════════════════════════════════ */
export const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlog();
    axios.get(`${API}/cities`).then(r => setCities(r.data.cities || [])).catch(() => {});
  }, [slug]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blogs/${slug}`);
      setBlog(res.data.blog);
    } catch {
      toast.error('Blog not found');
      navigate('/blogs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={S.detailPage}>
      <style>{S.fontFace + S.keyframes}</style>
      <div style={{ ...S.detailHero }}><div className="bl-skeleton" style={{ width: '100%', height: '100%' }} /></div>
      <div style={S.detailBody}>
        <div className="bl-skeleton" style={{ height: '24px', width: '70%', marginBottom: '16px' }} />
        <div className="bl-skeleton" style={{ height: '14px', width: '100%', marginBottom: '8px' }} />
        <div className="bl-skeleton" style={{ height: '14px', width: '90%' }} />
      </div>
    </div>
  );
  if (!blog) return null;

  return (
    <div style={S.detailPage}>
      <style>{S.fontFace + S.keyframes}</style>
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} />

      {/* Back bar */}
      <div style={S.backBar}>
        <div style={S.backBarInner}>
          <button style={S.backBtn} onClick={() => navigate('/blogs')}>
            <ChevronLeft size={15} /> Back to Blogs
          </button>
        </div>
      </div>

      {/* Hero cover */}
      <div style={S.detailHero}>
        {blog.cover_image
          ? <img src={blog.cover_image} alt={blog.title} style={S.detailCover} />
          : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#1a1a1a,#333)' }} />
        }
        <div style={S.detailHeroOverlay} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '36px 24px' }}>
          <div style={{ maxWidth: '860px', margin: '0 auto' }}>
            {blog.tags?.[0] && <span style={S.detailHeroTag}>{blog.tags[0]}</span>}
            <h1 style={S.detailTitle}>{blog.title}</h1>
            <div style={S.detailMeta}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ ...S.detailAvatar }}>
                  <Avatar src={blog.author_avatar} name={blog.written_by} size={40} />
                </div>
                <div>
                  <p style={S.detailAuthorName}>{blog.written_by}</p>
                  <p style={S.detailAuthorRole}>{blog.author_role}</p>
                </div>
              </div>
              <div style={S.detailMetaDivider} />
              <div style={S.detailMetaItem}><Calendar size={13} />{formatDate(blog.created_at)}</div>
              <div style={S.detailMetaDivider} />
              <div style={S.detailMetaItem}><Clock size={13} />{readTime(blog.content)} min read</div>
              {blog.views > 0 && <>
                <div style={S.detailMetaDivider} />
                <div style={S.detailMetaItem}><Eye size={13} />{blog.views} views</div>
              </>}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={S.detailBody}>
        {blog.description && <p style={S.detailDesc}>{blog.description}</p>}
        <div
          style={S.detailContent}
          dangerouslySetInnerHTML={{ __html: blog.content.replace(/\n/g, '<br/>') }}
        />
        {blog.tags?.length > 0 && (
          <div style={S.detailTagsRow}>
            {blog.tags.map(t => (
              <span key={t} onClick={() => navigate(`/blogs?tag=${t}`)} style={{ ...S.tag(false), cursor: 'pointer' }} className="bl-tag">
                <Tag size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />{t}
              </span>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   BLOG LIST PAGE  (default export)
══════════════════════════════════════════════════════════════════════════════ */
const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [page, setPage]         = useState(0);
  const [cities, setCities]     = useState([]);
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [allTags, setAllTags]   = useState([]);

  const LIMIT = 9;

  useEffect(() => {
    axios.get(`${API}/cities`).then(r => setCities(r.data.cities || [])).catch(() => {});
    // get all tags from first big fetch
    axios.get(`${API}/blogs?limit=200`).then(r => {
      const tags = [...new Set((r.data.blogs || []).flatMap(b => b.tags || []))].filter(Boolean);
      setAllTags(tags);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [search, activeTag, page]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, skip: page * LIMIT });
      if (search)    params.set('search', search);
      if (activeTag) params.set('tag', activeTag);
      const res = await axios.get(`${API}/blogs?${params}`);
      setBlogs(res.data.blogs || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => { setSearch(e.target.value); setPage(0); };
  const handleTag    = (t) => { setActiveTag(prev => prev === t ? '' : t); setPage(0); };

  const featured = blogs[0];
  const rest     = blogs.slice(1);
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={S.page}>
      <style>{S.fontFace + S.keyframes}</style>
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} />

      {/* Hero */}
      <section style={S.hero}>
        <div style={S.heroGrid} />
        <div style={S.heroGlow} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="bl-fade" style={S.heroBadge}>
            <span style={S.heroDot} />
            Insights & Guides
          </div>
          <h1 className="bl-fade bl-fade1" style={S.heroTitle}>
            The X7 Autoz <span style={S.heroAccent}>Blog</span>
          </h1>
          <p className="bl-fade bl-fade2" style={S.heroSub}>
            Tips, guides, and stories from the world of buying and selling vehicles — curated by our team.
          </p>
          {/* Search */}
          <div className="bl-fade bl-fade3" style={S.searchWrap}>
            <div style={S.searchBox}>
              <Search size={16} color="#bbb" />
              <input
                className="bl-search-input"
                style={S.searchInput}
                placeholder="Search articles..."
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>
          {/* Tags */}
          {allTags.length > 0 && (
            <div style={S.tagsRow}>
              {allTags.map(t => (
                <button key={t} style={S.tag(activeTag === t)} className="bl-tag" onClick={() => handleTag(t)}>
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section style={S.section}>
        <div style={S.inner}>

          {/* Loading skeletons */}
          {loading && (
            <div style={S.grid}>
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {/* Empty */}
          {!loading && blogs.length === 0 && (
            <div style={S.empty}>
              <div style={S.emptyIcon}>📝</div>
              <h3 style={S.emptyTitle}>No articles found</h3>
              <p style={S.emptySub}>{search || activeTag ? 'Try a different search or tag.' : 'Check back soon — articles are on the way!'}</p>
            </div>
          )}

          {/* Featured card */}
          {!loading && featured && page === 0 && !search && !activeTag && (
            <div
              className="bl-card"
              style={{ ...S.featuredCard, cursor: 'pointer' }}
              onClick={() => navigate(`/blogs/${featured.slug}`)}
            >
              <div style={S.featuredImg}>
                {featured.cover_image
                  ? <img src={featured.cover_image} alt={featured.title} style={{ ...S.featuredImgEl }} className="bl-card-img" />
                  : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,rgba(255,90,0,0.08),rgba(180,180,180,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '64px' }}>✍️</div>
                }
              </div>
              <div style={S.featuredBody}>
                <p style={S.featuredLabel}>✦ Latest Article</p>
                <h2 style={S.featuredTitle}>{featured.title}</h2>
                <p style={S.featuredDesc}>{featured.description}</p>
                <div style={S.featuredMeta}>
                  <div style={S.metaAvatar}>
                    <Avatar src={featured.author_avatar} name={featured.written_by} size={36} />
                  </div>
                  <div>
                    <p style={S.metaName}>{featured.written_by}</p>
                    <p style={S.metaText}>{featured.author_role} · {formatDate(featured.created_at)} · {readTime(featured.content || featured.description)} min read</p>
                  </div>
                </div>
                <button
                  className="bl-read-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#ff5a00', border: 'none', borderRadius: '12px', padding: '12px 22px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', transition: 'background 0.15s, transform 0.12s', fontFamily: "'Satoshi', sans-serif" }}
                >
                  Read Article <ArrowRight size={15} className="bl-card-arrow" />
                </button>
              </div>
            </div>
          )}

          {/* Cards grid */}
          {!loading && (
            <div style={S.grid}>
              {(page === 0 && !search && !activeTag ? rest : blogs).map((blog, idx) => (
                <div
                  key={blog.id}
                  className="bl-card"
                  style={{ ...S.card, animationDelay: `${idx * 0.05}s` }}
                  onClick={() => navigate(`/blogs/${blog.slug}`)}
                >
                  <div style={S.cardImgWrap}>
                    {blog.cover_image
                      ? <img src={blog.cover_image} alt={blog.title} style={S.cardImg} className="bl-card-img" />
                      : <div style={S.cardImgPlaceholder}><span style={{ fontSize: '44px' }}>✍️</span></div>
                    }
                  </div>
                  <div style={S.cardBody}>
                    {blog.tags?.length > 0 && (
                      <div style={S.cardTags}>
                        {blog.tags.slice(0, 2).map(t => <span key={t} style={S.cardTagPill}>{t}</span>)}
                      </div>
                    )}
                    <h3 style={S.cardTitle}>{blog.title}</h3>
                    <p style={S.cardDesc}>{blog.description}</p>
                    <div style={S.cardFooter}>
                      <div style={S.cardAuthor}>
                        <div style={S.cardAvatar}>
                          <Avatar src={blog.author_avatar} name={blog.written_by} size={30} />
                        </div>
                        <div>
                          <p style={S.cardAuthorName}>{blog.written_by}</p>
                          <p style={S.cardDate}>{formatDate(blog.created_at)} · {readTime(blog.content || blog.description)} min</p>
                        </div>
                      </div>
                      <div style={S.cardArrow} className="bl-card-arrow">
                        <ArrowRight size={14} color="#ff5a00" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div style={S.paginationRow}>
              {page > 0 && (
                <button style={S.pageBtn(false)} onClick={() => setPage(p => p - 1)}>‹</button>
              )}
              {Array.from({ length: totalPages }).map((_, i) => (
                <button key={i} style={S.pageBtn(i === page)} onClick={() => setPage(i)}>{i + 1}</button>
              ))}
              {page < totalPages - 1 && (
                <button style={S.pageBtn(false)} onClick={() => setPage(p => p + 1)}>›</button>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blogs;